import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDependencies } from '@servicetitan/react-ioc';
import { FinancingApi } from '@servicetitan/financing-api-client';
import { DecisionDataStore } from '../../stores/DecisionDataStore';

export interface UseCreateAccountOptions {
    /** Callback when account creation succeeds */
    onSuccess?: () => void;
    /** Callback when account creation fails */
    onError?: (error: Error) => void;
}

export interface UseCreateAccountResult {
    /** Function to create the account */
    createAccount: () => Promise<void>;
    /** Whether the account creation is in progress */
    isLoading: boolean;
    /** Whether the account creation was successful */
    isSuccess: boolean;
    /** Error that occurred during account creation */
    error: Error | null;
    /** Reset the error state */
    resetError: () => void;
}

/**
 * Custom hook for creating a customer account with the provider
 *
 * Provides functionality to create a customer account by calling the
 * loansCreateAccount API endpoint. This is a GreenSky-specific action.
 */
export const useCreateAccount = ({
    onSuccess,
    onError,
}: UseCreateAccountOptions = {}): UseCreateAccountResult => {
    const [error, setError] = useState<Error | null>(null);
    const [apiClient] = useDependencies(FinancingApi);
    const { decisionData } = DecisionDataStore.useStore();

    const mutation = useMutation({
        mutationFn: async (): Promise<void> => {
            if (!decisionData?.loanId) {
                throw new Error('No loan ID available for account creation');
            }

            // Call the account creation API
            await apiClient.loansCreateAccount(decisionData.loanId);
        },
        onSuccess: () => {
            setError(null);
            onSuccess?.();
        },
        onError: (err: unknown) => {
            const errorObj = err instanceof Error ? err : new Error('Failed to create account');
            setError(errorObj);
            onError?.(errorObj);
        },
    });

    const createAccount = useCallback(async (): Promise<void> => {
        setError(null);
        return mutation.mutateAsync();
    }, [mutation]);

    const resetError = useCallback(() => {
        setError(null);
        mutation.reset();
    }, [mutation]);

    return {
        createAccount,
        isLoading: mutation.isPending,
        isSuccess: mutation.isSuccess,
        error,
        resetError,
    };
};
